import React from 'react';
import { useEditorStore } from '../../store/editorStore';
import { track, EVENTS } from '../../lib/mixpanel';
import { Button } from '../UI/Button';
import styles from './CropTool.module.css';

interface CropToolProps {
  imageWidth: number;
  imageHeight: number;
}

export const CropTool: React.FC<CropToolProps> = ({ imageWidth, imageHeight }) => {
  const { crop, setCrop, resetCrop } = useEditorStore();

  const presets = [
    { label: 'Free', ratio: null },
    { label: '1:1', ratio: 1 },
    { label: '4:3', ratio: 4 / 3 },
    { label: '3:4', ratio: 3 / 4 },
    { label: '16:9', ratio: 16 / 9 },
    { label: '9:16', ratio: 9 / 16 },
  ];

  // Margin to keep crop handles visible within the canvas.
  // Keep in sync with CROP_HANDLE_MARGIN in EditorModal.
  const MARGIN = 10;

  const enableCrop = () => {
    const maxW = imageWidth - MARGIN * 2;
    const maxH = imageHeight - MARGIN * 2;
    setCrop({
      enabled: true,
      x: imageWidth * 0.1,
      y: imageHeight * 0.1,
      width: Math.min(imageWidth * 0.8, maxW),
      height: Math.min(imageHeight * 0.8, maxH),
    });
  };

  const applyPreset = (ratio: number | null) => {
    if (!crop.enabled) enableCrop();
    track(EVENTS.CROP_RATIO_SELECTED, { ratio: ratio === null ? 'free' : ratio });
    if (ratio === null) return;

    const maxW = imageWidth - MARGIN * 2;
    const maxH = imageHeight - MARGIN * 2;
    const w = maxW;
    const h = w / ratio;
    const clampedH = Math.min(h, maxH);
    const clampedW = clampedH * ratio;

    setCrop({
      enabled: true,
      x: (imageWidth - clampedW) / 2,
      y: (imageHeight - clampedH) / 2,
      width: clampedW,
      height: clampedH,
    });
  };

  const toggleCrop = () => {
    const enabling = !crop.enabled;
    track(EVENTS.CROP_TOGGLED, { enabled: enabling });
    if (crop.enabled) resetCrop();
    else enableCrop();
  };

  return (
    <div className={styles['container']}>
      <div className={styles['header']}>
        <h3 className={styles['title']}>Crop & Resize</h3>
        <button
          onClick={toggleCrop}
          className={[
            styles['toggle-track'],
            crop.enabled ? styles['toggle-track-on'] : styles['toggle-track-off'],
          ].join(' ')}
        >
          <span
            className={[
              styles['toggle-knob'],
              crop.enabled ? styles['toggle-knob-on'] : styles['toggle-knob-off'],
            ].join(' ')}
          />
        </button>
      </div>

      {!crop.enabled && (
        <p className={styles['hint']}>
          Enable cropping to select a region. Drag the handles on the canvas to adjust.
        </p>
      )}

      {crop.enabled && (
        <>
          <p className={styles['hint']}>
            Drag the crop rectangle or its handles on the canvas to adjust the region.
          </p>

          <div>
            <p className={styles['ratio-label']}>Aspect Ratio</p>
            <div className={styles['preset-grid']}>
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => applyPreset(preset.ratio)}
                  className={styles['preset-btn']}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles['dimensions-grid']}>
            <div className={styles['dim-box']}>
              <span className={styles['dim-label']}>Width</span>
              <span className={styles['dim-value']}>{Math.round(crop.width)}px</span>
            </div>
            <div className={styles['dim-box']}>
              <span className={styles['dim-label']}>Height</span>
              <span className={styles['dim-value']}>{Math.round(crop.height)}px</span>
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={resetCrop}>
            Remove Crop
          </Button>
        </>
      )}
    </div>
  );
};
